import { useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useCompany, useCompanyHistory } from "@/hooks/use-companies";
import { useSubmitAnalysis } from "@/hooks/use-analysis";
import { getScore } from "@/api/analysis";
import { generateReport, getReports } from "@/api/reports";
import { ScoreGauge } from "@/components/analysis/score-gauge";
import { FiscalRadarChart } from "@/components/analysis/radar-chart";
import { formatCnpj, formatCurrency, cn, getRiskBg } from "@/lib/utils";
import {
  ArrowLeft, RefreshCw, FileText, AlertTriangle, Shield,
  TrendingUp, Building2, MapPin, Calendar, Hash,
  CheckCircle2, XCircle, Clock, Loader2, ChevronRight,
  Info, Zap, Users, DollarSign, Briefcase,
} from "lucide-react";

const AXIS_LABELS: Record<string, { label: string; description: string; icon: typeof Shield }> = {
  federal: { label: "Federal", description: "PGFN debt, CAPAG, RFB status", icon: Shield },
  estadual: { label: "Estadual", description: "ICMS, state registration", icon: MapPin },
  documentos: { label: "Documentos", description: "CND Federal/Estadual/Municipal", icon: FileText },
  obrigacoes: { label: "Obrigacoes", description: "PGDASD, DEFIS, DCTFWeb filings", icon: Calendar },
  contingencias: { label: "Contingencias", description: "Judicial, protests, guarantees", icon: AlertTriangle },
  regime_reforma: { label: "Regime", description: "Tax regime fit, reform exposure", icon: TrendingUp },
};

function ScoreBar({ label, score, description }: { label: string; score: number; description: string }) {
  const color = score > 70 ? "bg-green-500" : score > 50 ? "bg-amber-500" : score > 25 ? "bg-orange-500" : "bg-red-500";
  const textColor = score > 70 ? "text-green-700" : score > 50 ? "text-amber-700" : score > 25 ? "text-orange-700" : "text-red-700";
  const available = score !== 50; // 50 = neutral/no data

  return (
    <div className="flex items-center gap-4">
      <div className="w-28 text-sm font-medium text-slate-700">{label}</div>
      <div className="flex-1">
        <div className="h-2.5 bg-slate-100 rounded-full overflow-hidden">
          <div className={cn("h-full rounded-full transition-all", color)} style={{ width: `${score}%` }} />
        </div>
      </div>
      <div className="w-12 text-right">
        <span className={cn("text-sm font-semibold", textColor)}>{score}</span>
      </div>
      <div className="w-48 text-xs text-slate-400 hidden lg:block">
        {available ? description : "Awaiting data source"}
      </div>
    </div>
  );
}

export function CompanyDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const companyId = parseInt(id || "0");

  const { data: companyData, isLoading, error } = useCompany(companyId);
  const { data: scoreData } = useQuery({
    queryKey: ["score", companyId],
    queryFn: () => getScore(companyId),
    enabled: companyId > 0,
    staleTime: 300_000,
  });
  const { data: historyData } = useCompanyHistory(companyId);
  const { data: reportsData } = useQuery({
    queryKey: ["reports", { companyId }],
    queryFn: () => getReports({ companyId }),
    enabled: companyId > 0,
  });

  const reanalyze = useSubmitAnalysis();
  const genReport = useMutation({
    mutationFn: () => generateReport({ companyId, type: "company_analysis" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["reports"] }),
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-6 h-6 text-slate-400 animate-spin" />
      </div>
    );
  }

  if (error || !companyData?.company) {
    return (
      <div className="max-w-3xl mx-auto text-center py-20">
        <XCircle className="w-10 h-10 text-slate-300 mx-auto mb-3" />
        <p className="text-slate-500">Company not found or failed to load</p>
        <Link to="/" className="text-sm text-blue-500 hover:text-blue-600 mt-2 inline-block">Back to portfolio</Link>
      </div>
    );
  }

  const company = companyData.company;
  const s = scoreData?.snapshot || companyData.latestSnapshot;
  const snap = s as Record<string, unknown> | null;
  const reports = reportsData?.reports || [];
  const history = historyData?.snapshots || [];

  const companyName = String(company.razao_social || company.nome_fantasia || formatCnpj(company.cnpj));

  const handleReanalyze = () => {
    reanalyze.mutate(company.cnpj, {
      onSuccess: () => {
        qc.invalidateQueries({ queryKey: ["company", companyId] });
        qc.invalidateQueries({ queryKey: ["score", companyId] });
      },
    });
  };

  // Parse snapshot data safely
  const positiveFactors: string[] = snap ? JSON.parse(String(snap.fatores_positivos || "[]")) : [];
  const negativeFactors: string[] = snap ? JSON.parse(String(snap.fatores_negativos || "[]")) : [];
  const findings: Array<Record<string, unknown>> = snap ? JSON.parse(String(snap.findings_json || "[]")) : [];
  const recommendations: string[] = snap ? JSON.parse(String(snap.recomendacoes || "[]")) : [];

  // Parse SERPRO cnpj_data_json safely
  let cnpjData: Record<string, unknown> | null = null;
  try {
    if (company.cnpj_data_json) {
      cnpjData = JSON.parse(company.cnpj_data_json);
    }
  } catch {
    cnpjData = null;
  }
  const socios = Array.isArray(cnpjData?.socios) ? (cnpjData!.socios as Array<Record<string, string>>) : [];
  const capitalSocial = typeof cnpjData?.capitalSocial === "number" ? (cnpjData!.capitalSocial as number) / 100 : null;
  const situacaoEspecial = cnpjData?.situacaoEspecial ? String(cnpjData.situacaoEspecial) : null;
  const dataSituacaoEspecial = cnpjData?.dataSituacaoEspecial ? String(cnpjData.dataSituacaoEspecial) : null;
  const cnaeSecundarias = Array.isArray(cnpjData?.cnaeSecundarias) ? (cnpjData!.cnaeSecundarias as Array<Record<string, string | number>>) : [];

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/" className="p-2 rounded-lg hover:bg-slate-100 transition-colors">
            <ArrowLeft className="w-5 h-5 text-slate-500" />
          </Link>
          <div>
            <h1 className="text-2xl font-semibold text-slate-900">{companyName}</h1>
            <div className="flex items-center gap-3 mt-1">
              <span className="text-sm text-slate-500 font-mono">{formatCnpj(company.cnpj)}</span>
              {company.uf && (
                <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded">{company.uf}</span>
              )}
              {company.situacao_cadastral && (
                <span className={cn("text-xs px-2 py-0.5 rounded border",
                  company.situacao_cadastral === "ATIVA" || company.situacao_cadastral === "02"
                    ? "bg-green-50 text-green-700 border-green-200"
                    : "bg-red-50 text-red-700 border-red-200"
                )}>
                  {company.situacao_cadastral === "02" ? "ATIVA" : company.situacao_cadastral}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleReanalyze}
            disabled={reanalyze.isPending}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-50"
          >
            {reanalyze.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            Re-analyze
          </button>
          <button
            onClick={() => genReport.mutate()}
            disabled={genReport.isPending}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm bg-slate-900 text-white rounded-lg hover:bg-slate-800 disabled:opacity-50"
          >
            {genReport.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
            Generate Report
          </button>
        </div>
      </div>

      {/* Company Info Card */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <h3 className="text-sm font-medium text-slate-900 mb-4 flex items-center gap-2">
          <Building2 className="w-4 h-4 text-slate-500" /> Company Registration (SERPRO)
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-3">
          {[
            { label: "Razao Social", value: company.razao_social },
            { label: "Nome Fantasia", value: company.nome_fantasia },
            { label: "CNPJ", value: formatCnpj(company.cnpj) },
            { label: "Situacao Cadastral", value: company.situacao_cadastral },
            { label: "Data Abertura", value: company.data_abertura },
            { label: "CNAE Principal", value: company.cnae_principal },
            { label: "Natureza Juridica", value: company.natureza_juridica },
            { label: "Porte", value: company.porte },
            { label: "UF", value: company.uf },
            { label: "Endereco", value: company.endereco },
            { label: "Telefone", value: company.telefone },
          ].map(({ label, value }) => (
            <div key={label} className="flex flex-col">
              <span className="text-xs text-slate-400 uppercase tracking-wide">{label}</span>
              <span className={cn("text-sm mt-0.5", value && value.trim() ? "text-slate-900" : "text-slate-300 italic")}>
                {value && value.trim() ? value : "—"}
              </span>
            </div>
          ))}
        </div>
        {!company.razao_social && (
          <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 text-xs text-blue-700 flex items-center gap-2">
            <Info className="w-3.5 h-3.5 flex-shrink-0" />
            Company data will be populated after a successful analysis. Click "Re-analyze" to fetch data from SERPRO.
          </div>
        )}
      </div>

      {/* Re-analysis status */}
      {reanalyze.isSuccess && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 flex items-center gap-2 text-sm text-blue-700">
          <Clock className="w-4 h-4" />
          Analysis queued. Results will appear in 30-60 seconds. Refresh the page.
        </div>
      )}

      {/* Partners (QSA) */}
      {cnpjData && (
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h3 className="text-sm font-medium text-slate-900 mb-4 flex items-center gap-2">
            <Users className="w-4 h-4 text-slate-500" /> Partners (QSA)
          </h3>
          {socios.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-100">
                    <th className="text-left text-xs font-medium text-slate-400 uppercase tracking-wide pb-2">Nome</th>
                    <th className="text-left text-xs font-medium text-slate-400 uppercase tracking-wide pb-2">Qualificação</th>
                    <th className="text-left text-xs font-medium text-slate-400 uppercase tracking-wide pb-2">Data Entrada</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {socios.map((s, i) => (
                    <tr key={i}>
                      <td className="py-2 text-slate-900">{s.nome || "—"}</td>
                      <td className="py-2 text-slate-600">{s.qualificacao || "—"}</td>
                      <td className="py-2 text-slate-600">{s.dataEntrada || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-sm text-slate-400">No partner data available</p>
          )}
        </div>
      )}

      {/* Financial */}
      {cnpjData && (
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h3 className="text-sm font-medium text-slate-900 mb-4 flex items-center gap-2">
            <DollarSign className="w-4 h-4 text-slate-500" /> Financial
          </h3>
          <div className="flex flex-wrap gap-x-8 gap-y-3">
            <div className="flex flex-col">
              <span className="text-xs text-slate-400 uppercase tracking-wide">Capital Social</span>
              <span className="text-sm mt-0.5 text-slate-900">
                {capitalSocial !== null ? formatCurrency(capitalSocial) : "—"}
              </span>
            </div>
            <div className="flex flex-col">
              <span className="text-xs text-slate-400 uppercase tracking-wide">Situação Especial</span>
              {situacaoEspecial && situacaoEspecial.trim() ? (
                <span className="text-xs mt-1 inline-block px-2 py-0.5 rounded-full bg-red-50 text-red-700 border border-red-200 font-medium w-fit">
                  {situacaoEspecial}
                </span>
              ) : (
                <span className="text-sm mt-0.5 text-slate-300 italic">—</span>
              )}
            </div>
            <div className="flex flex-col">
              <span className="text-xs text-slate-400 uppercase tracking-wide">Data Situação Especial</span>
              <span className={cn("text-sm mt-0.5", dataSituacaoEspecial && dataSituacaoEspecial.trim() ? "text-slate-900" : "text-slate-300 italic")}>
                {dataSituacaoEspecial && dataSituacaoEspecial.trim() ? dataSituacaoEspecial : "—"}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Secondary Activities (CNAEs) */}
      {cnpjData && cnaeSecundarias.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h3 className="text-sm font-medium text-slate-900 mb-4 flex items-center gap-2">
            <Briefcase className="w-4 h-4 text-slate-500" /> Secondary Activities (CNAEs)
          </h3>
          <div className="flex flex-wrap gap-2">
            {cnaeSecundarias.map((cnae, i) => (
              <span
                key={i}
                className="text-xs bg-slate-100 text-slate-700 px-2.5 py-1 rounded-full border border-slate-200"
              >
                {cnae.codigo} - {cnae.descricao}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Report generated */}
      {genReport.isSuccess && (
        <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-3 flex items-center justify-between text-sm text-green-700">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4" />
            Report generated successfully
          </div>
          <button onClick={() => navigate("/reports")} className="text-green-800 font-medium hover:underline flex items-center gap-1">
            View reports <ChevronRight className="w-3 h-3" />
          </button>
        </div>
      )}

      {snap ? (
        <>
          {/* Score + Radar */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Score gauge */}
            <div className="bg-white rounded-xl border border-slate-200 p-6 flex flex-col items-center justify-center">
              <ScoreGauge score={snap.score_overall as number} size={220} />
              <div className={cn("mt-2 text-sm font-semibold px-3 py-1 rounded-full border", getRiskBg(snap.risk_level as string))}>
                {snap.risk_level as string} RISK
              </div>
              {snap.deal_killer_override ? (
                <div className="mt-3 bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-xs text-red-700 text-center">
                  <AlertTriangle className="w-3.5 h-3.5 inline mr-1" />
                  Deal-killer override active
                </div>
              ) : null}
            </div>

            {/* Radar chart */}
            <div className="bg-white rounded-xl border border-slate-200 p-6 col-span-1 lg:col-span-2">
              <h3 className="text-sm font-medium text-slate-900 mb-4">6-Axis Fiscal Health</h3>
              <FiscalRadarChart
                scores={{
                  federal: snap.score_federal as number ?? 50,
                  estadual: snap.score_estadual as number ?? 50,
                  documentos: snap.score_documentos as number ?? 50,
                  obrigacoes: snap.score_obrigacoes as number ?? 50,
                  contingencias: snap.score_contingencias as number ?? 50,
                  regime_reforma: snap.score_regime_reforma as number ?? 50,
                }}
                height={320}
              />
            </div>
          </div>

          {/* Axis Breakdown */}
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <h3 className="text-sm font-medium text-slate-900 mb-4">Score Breakdown by Axis</h3>
            <div className="space-y-3">
              {Object.entries(AXIS_LABELS).map(([key, { label, description }]) => (
                <ScoreBar
                  key={key}
                  label={label}
                  score={(snap as Record<string, unknown>)[`score_${key}`] as number ?? 50}
                  description={description}
                />
              ))}
            </div>
          </div>

          {/* Two-column: Positive + Negative factors */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h3 className="text-sm font-medium text-green-700 mb-3 flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4" /> Positive Factors
              </h3>
              {positiveFactors.length > 0 ? (
                <ul className="space-y-2">
                  {positiveFactors.map((f, i) => (
                    <li key={i} className="text-sm text-slate-700 flex items-start gap-2">
                      <span className="w-1.5 h-1.5 bg-green-500 rounded-full mt-1.5 flex-shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-slate-400">No positive factors identified</p>
              )}
            </div>

            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h3 className="text-sm font-medium text-red-700 mb-3 flex items-center gap-2">
                <XCircle className="w-4 h-4" /> Risk Factors
              </h3>
              {negativeFactors.length > 0 ? (
                <ul className="space-y-2">
                  {negativeFactors.map((f, i) => (
                    <li key={i} className="text-sm text-slate-700 flex items-start gap-2">
                      <span className="w-1.5 h-1.5 bg-red-500 rounded-full mt-1.5 flex-shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-slate-400">No risk factors identified</p>
              )}
            </div>
          </div>

          {/* Findings table */}
          {findings.length > 0 && (
            <div className="bg-white rounded-xl border border-slate-200">
              <div className="px-6 py-4 border-b border-slate-100">
                <h3 className="text-sm font-medium text-slate-900">Findings ({findings.length})</h3>
              </div>
              <div className="divide-y divide-slate-100">
                {findings.map((f, i) => (
                  <div key={i} className="px-6 py-4">
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-sm font-medium text-slate-900">{String(f.description)}</p>
                      <span className={cn("text-xs font-medium px-2 py-0.5 rounded-full border",
                        getRiskBg(f.dealImpact === "DEAL_KILLER" ? "CRITICO" : f.dealImpact === "PRICE_ADJUSTMENT" ? "ALTO" : "MEDIO")
                      )}>
                        {String(f.dealImpact)}
                      </span>
                    </div>
                    <p className="text-xs text-slate-500">{String(f.axis)} / {String(f.category)} — {String(f.classification)}</p>
                    {f.recommendation ? (
                      <p className="text-xs text-blue-600 mt-1">{String(f.recommendation)}</p>
                    ) : null}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Recommendations */}
          {recommendations.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="text-sm font-medium text-amber-800 mb-3 flex items-center gap-2">
                <Info className="w-4 h-4" /> Recommendations
              </h3>
              <ul className="space-y-2">
                {recommendations.map((r, i) => (
                  <li key={i} className="text-sm text-amber-900">{r}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Analysis coverage summary */}
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-6">
            <h3 className="text-sm font-medium text-slate-700 mb-3 flex items-center gap-2">
              <Zap className="w-4 h-4" /> Analysis Coverage
            </h3>
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
              {[
                { name: "Federal Tax Compliance", status: "active" },
                { name: "State Tax (ICMS)", status: "active" },
                { name: "Tax Clearance Certificates", status: "active" },
                { name: "Labor Compliance (FGTS/CNDT)", status: "active" },
                { name: "Active Debt & Contingencies", status: "active" },
                { name: "Tax Regime & Reform Exposure", status: "active" },
              ].map((src) => (
                <div key={src.name} className="flex items-center gap-2 text-xs">
                  <CheckCircle2 className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />
                  <span className="text-slate-700">{src.name}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Reports for this company */}
          {reports.length > 0 && (
            <div className="bg-white rounded-xl border border-slate-200">
              <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
                <h3 className="text-sm font-medium text-slate-900">Reports ({reports.length})</h3>
                <button onClick={() => navigate("/reports")} className="text-xs text-blue-500 hover:text-blue-600">
                  View all
                </button>
              </div>
              <div className="divide-y divide-slate-100">
                {reports.slice(0, 3).map((r: Record<string, unknown>) => (
                  <div key={r.id as number} className="px-6 py-3 flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-900">{String(r.title)}</p>
                      <p className="text-xs text-slate-400">
                        {String(r.model_used)} — {new Date(String(r.created_at)).toLocaleDateString("pt-BR")}
                      </p>
                    </div>
                    <button
                      onClick={() => navigate("/reports")}
                      className="text-xs text-blue-500 hover:text-blue-600"
                    >
                      View
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Analysis metadata */}
          <div className="text-xs text-slate-400 flex items-center gap-4">
            <span>Last analysis: {snap.created_at ? new Date(String(snap.created_at)).toLocaleString("pt-BR") : "N/A"}</span>
            <span>Source: {String(snap.source)}</span>
            <span>Snapshot #{String(snap.id)}</span>
          </div>
        </>
      ) : (
        /* No snapshot yet */
        <div className="bg-white rounded-xl border border-slate-200 px-6 py-16 text-center">
          <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-slate-900 mb-1">No analysis data yet</h3>
          <p className="text-sm text-slate-500 mb-4">
            Click "Re-analyze" to start the fiscal health assessment for this company.
          </p>
          <button
            onClick={handleReanalyze}
            disabled={reanalyze.isPending}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-slate-900 text-white text-sm font-medium rounded-lg hover:bg-slate-800 disabled:opacity-50"
          >
            {reanalyze.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
            Start Analysis
          </button>
        </div>
      )}
    </div>
  );
}
